// Mailing-Liste pflegen (DCP-2228 Folge-Feature) — Zentrale und Administratoren
//
// Die Excel-Liste bleibt die maßgebliche Quelle: ein Import ersetzt den ganzen
// Bestand. Was hier von Hand angelegt wird, verschwindet also beim nächsten Import,
// wenn es nicht in der Datei steht. Der Warnkasten oben sagt das dauerhaft, nicht
// erst im Löschdialog — es ist die einzige Stelle, an der man das erfahren kann.
//
// ⚠️ Anders als bei /users lässt sich die Verweigerung hier NICHT an der Antwort
// ablesen: GET /api/mailings ist absichtlich für jede Rolle offen (jeder VA-Dialog
// liest es). Die Abfrage gelingt also auch einem Partner, und die Rolle muss vor dem
// Rendern selbst geprüft werden. `isDenied` bleibt für die Schreibfehler.
//
// Die Tabelle zeigt nicht alle acht Felder als eigene Spalte — das würde zu breit.
// Zeitraum 1/2 stehen als "von–bis" zusammengefasst, Eingabe- und Versanddatum
// bleiben dem Bearbeiten-Dialog vorbehalten.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertTriangle, ArrowLeft, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ConfirmationDialog } from '@/components/ui/confirmation-dialog';
import { useToast } from '@/hooks/use-toast';
import { useMe } from '@/hooks/usePermissions';
import { ApiError } from '@/lib/api-client';
import { cn } from '@/lib/utils';
import {
  useMailings,
  useSaveMailing,
  useDeleteMailings,
} from '@/services/event-service';
import { formatDay } from '@/features/events/mailing-match';
import { Role, type Mailing, type SaveMailingRequest } from '@/types';
import { MailingFormDialog } from './MailingFormDialog';
import { MailingImportControl } from './MailingImportControl';
import {
  pruneSelection,
  selectionState,
  toggleAll,
  toggleOne,
} from './selection';

/** "von–bis", oder "–" wenn der Zeitraum nicht gesetzt ist (Zeitraum 2). */
function formatPeriod(from: string | undefined, to: string | undefined): string {
  if (!from || !to) return '–';
  return `${formatDay(from)}–${formatDay(to)}`;
}

export function MailingListPage() {
  const { t } = useTranslation('settings');
  const { toast } = useToast();

  const { data: me } = useMe();
  const canManage = me?.role === Role.Zentral || me?.role === Role.Admin;

  const { data: mailings = [], isLoading, isError, error } = useMailings();
  const saveMutation = useSaveMailing();
  const deleteMutation = useDeleteMailings();

  const [selected, setSelected] = useState<string[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Mailing | null>(null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string[] | null>(null);

  const titles = useMemo(() => mailings.map((m) => m.title), [mailings]);

  // Gegen die frisch geladene Liste schneiden: nach einem Löschen oder Import kann
  // die Auswahl Bezeichnungen enthalten, die es nicht mehr gibt — dann zielt das
  // Massenlöschen ins Leere und das Kopf-Häkchen rechnet falsch.
  const liveSelection = useMemo(() => pruneSelection(selected, titles), [selected, titles]);

  const headerState = selectionState(mailings.length, liveSelection.length);
  const isDenied =
    error instanceof ApiError && (error.status === 401 || error.status === 403);

  // Heute als ISO-Tag, wie in mailing-match.ts: beide Werte sind reine Tage, damit
  // ist der Textvergleich der zeitliche. UTC-Abweichung kurz nach Mitternacht ist
  // bewusst dieselbe wie dort — das Abzeichen informiert, es sperrt nichts.
  const today = new Date().toISOString().slice(0, 10);

  const openCreate = () => {
    setEditing(null);
    setSaveError(null);
    setDialogOpen(true);
  };

  const openEdit = (mailing: Mailing) => {
    setEditing(mailing);
    setSaveError(null);
    setDialogOpen(true);
  };

  const handleSave = (req: SaveMailingRequest) => {
    setSaveError(null);
    saveMutation.mutate(req, {
      onSuccess: () => {
        toast({ title: t('mailings.toast.saved') });
        setDialogOpen(false);
      },
      // Serverfehler bleiben IM Dialog: die Kollisionsmeldung bezieht sich auf das
      // Feld, und ein Toast neben dem offenen Dialog mit dem strittigen Wert
      // verwirrt mehr als er hilft.
      onError: (e) => setSaveError(e instanceof Error ? e.message : String(e)),
    });
  };

  const handleDelete = (doomed: string[]) => {
    deleteMutation.mutate(doomed, {
      onSuccess: (result) => {
        setConfirmDelete(null);
        // Auswahl leeren: sonst bleiben gelöschte Bezeichnungen stehen.
        setSelected([]);

        toast({
          title: t('mailings.toast.deleted', { count: result.removed.length }),
        });

        // Teilfehler sichtbar machen — genau dafür gibt es die Antwort pro Eintrag.
        if (result.failed.length > 0) {
          toast({
            title: t('mailings.toast.deleteFailed'),
            description: result.failed.map((f) => `${f.title}: ${f.reason}`).join('\n'),
            variant: 'destructive',
          });
        }
      },
      onError: (e) =>
        toast({
          title: t('mailings.toast.error'),
          description: e instanceof Error ? e.message : String(e),
          variant: 'destructive',
        }),
    });
  };

  if (!canManage) {
    return (
      <div className="space-y-6">
        <h2 className="text-3xl font-bold tracking-tight">{t('mailings.page.title')}</h2>
        <p className="p-8 text-center text-muted-foreground">{t('mailings.page.noAccess')}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button asChild variant="ghost" size="sm" className="-ml-2 h-7 gap-1 px-2">
            <Link to="/settings">
              <ArrowLeft className="h-3.5 w-3.5" />
              {t('mailings.page.backToSettings')}
            </Link>
          </Button>
          <h2 className="text-3xl font-bold tracking-tight">{t('mailings.page.title')}</h2>
          <p className="text-muted-foreground">{t('mailings.page.description')}</p>
        </div>
        <Button className="gap-2" onClick={openCreate}>
          <Plus className="h-4 w-4" />
          {t('mailings.actions.create')}
        </Button>
      </div>

      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <p>{t('mailings.replaceWarning')}</p>
      </div>

      {/* Der Excel-Import wohnt hier, nicht in den Einstellungen: dort steht seit der
          Gruppierung nur eine Verweiszeile, und der Import gehört thematisch zur Liste,
          die er ersetzt. */}
      <MailingImportControl />

      {isLoading ? (
        <p className="p-8 text-center text-muted-foreground">{t('mailings.page.loading')}</p>
      ) : isDenied ? (
        <p className="p-8 text-center text-muted-foreground">{t('mailings.page.noAccess')}</p>
      ) : isError ? (
        <p className="p-8 text-center text-red-600">{t('mailings.page.loadError')}</p>
      ) : mailings.length === 0 ? (
        <p className="rounded-md border p-8 text-center text-muted-foreground">
          {t('mailings.page.empty')}
        </p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-12">
                <Checkbox
                  checked={headerState === 'all'}
                  onCheckedChange={() => setSelected(toggleAll(titles, liveSelection))}
                  aria-label={t('mailings.table.selectAll')}
                  className={cn(headerState === 'some' && 'data-[state=checked]:bg-primary/50')}
                />
              </TableHead>
              <TableHead>{t('mailings.table.title')}</TableHead>
              <TableHead>{t('mailings.table.period1')}</TableHead>
              <TableHead>{t('mailings.table.period2')}</TableHead>
              <TableHead>{t('mailings.table.onlineDate')}</TableHead>
              <TableHead>{t('mailings.table.status')}</TableHead>
              <TableHead className="text-right">{t('mailings.table.actions')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {mailings.map((mailing) => {
              const isSelected = liveSelection.includes(mailing.title);
              // Dieselbe Bedingung wie der erste Zweig von matchMailings(), nur ohne
              // VA-Datum: abgelaufen heißt, die Eingabefrist liegt in der
              // Vergangenheit. Ohne die Spalte sieht man nicht, welche Zeilen totes
              // Gewicht sind.
              const expired = mailing.cutoffDate < today;

              return (
                <TableRow key={mailing.title} className={cn(isSelected && 'bg-blue-50')}>
                  <TableCell>
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() =>
                        setSelected(toggleOne(liveSelection, mailing.title))
                      }
                      aria-label={t('mailings.table.selectRow', { title: mailing.title })}
                    />
                  </TableCell>
                  <TableCell className="font-medium">{mailing.title}</TableCell>
                  <TableCell>{formatPeriod(mailing.period1From, mailing.period1To)}</TableCell>
                  <TableCell>{formatPeriod(mailing.period2From, mailing.period2To)}</TableCell>
                  <TableCell>{formatDay(mailing.onlineDate)}</TableCell>
                  <TableCell>
                    <Badge variant={expired ? 'outline' : 'secondary'}>
                      {t(expired ? 'mailings.status.expired' : 'mailings.status.open')}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEdit(mailing)}
                      aria-label={t('mailings.actions.edit')}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setConfirmDelete([mailing.title])}
                      aria-label={t('mailings.actions.delete')}
                    >
                      <Trash2 className="h-4 w-4 text-red-600" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {/* Eigene Leiste statt BulkActionsBar: die hängt an useTranslation('reviews')
          und an Freigeben/Ablehnen. */}
      {liveSelection.length > 0 && (
        <div className="sticky bottom-0 z-40 flex items-center justify-between gap-4 border-t bg-background/95 p-3 backdrop-blur">
          <span className="text-sm text-muted-foreground">
            {t('mailings.bulk.selected', { count: liveSelection.length })}
          </span>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={() => setSelected([])}>
              {t('mailings.bulk.clearSelection')}
            </Button>
            <Button
              variant="destructive"
              size="sm"
              className="gap-2"
              onClick={() => setConfirmDelete([...liveSelection])}
            >
              <Trash2 className="h-4 w-4" />
              {t('mailings.bulk.deleteSelected')}
            </Button>
          </div>
        </div>
      )}

      <MailingFormDialog
        open={dialogOpen}
        mailing={editing}
        existingTitles={titles}
        saveError={saveError}
        onOpenChange={setDialogOpen}
        onSave={handleSave}
        isSaving={saveMutation.isPending}
      />

      {/* ConfirmationDialog hat keinen children-Slot, also Anzahl bzw. Bezeichnung
          per Interpolation. Reicht hier: die ausgewählten Zeilen stehen blau
          hinterlegt hinter dem Modal. */}
      <ConfirmationDialog
        open={confirmDelete !== null}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        title={t(
          confirmDelete?.length === 1 ? 'mailings.delete.title' : 'mailings.delete.titleMany'
        )}
        description={
          confirmDelete?.length === 1
            ? t('mailings.delete.descriptionOne', { title: confirmDelete[0] })
            : t('mailings.delete.descriptionMany', { count: confirmDelete?.length ?? 0 })
        }
        confirmLabel={t('mailings.delete.confirm')}
        cancelLabel={t('mailings.form.cancel')}
        variant="destructive"
        onConfirm={() => confirmDelete && handleDelete(confirmDelete)}
      />
    </div>
  );
}
